import { useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, CardHeader } from '@nextui-org/react';
import { supabase } from 'common/src/supabase';
import { getBrandAssets } from 'common/src/utils/branding';

// Pantalla de consentimiento del OAuth server de Supabase. Un cliente MCP
// (Claude, ChatGPT) manda al usuario acá con ?authorization_id=...; el
// usuario aprueba o rechaza y vuelve al cliente. Requiere sesión iniciada
// (App.tsx muestra el login antes si no la hay).
//
// Se usan los endpoints REST de GoTrue directamente porque la versión de
// supabase-js del monorepo (2.44) no trae los helpers auth.oauth.*.

interface AuthorizationDetails {
  authorization_id?: string;
  redirect_uri?: string;
  client?: { id: string; name: string };
  user?: { id: string; email: string };
  scope?: string;
  /** Presente cuando el consentimiento ya estaba otorgado (skip). */
  redirect_url?: string;
}

const SCOPE_LABELS: Record<string, string> = {
  openid: 'Confirmar tu identidad',
  email: 'Ver tu dirección de email',
  profile: 'Ver tu nombre de perfil',
};

const OAuthConsent = () => {
  const brand = getBrandAssets();
  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // El GET puede consumir la autorización (consent ya recordado): un solo
  // fetch aunque StrictMode monte el efecto dos veces.
  const fetchedRef = useRef(false);

  const authorizationId = new URLSearchParams(window.location.search).get(
    'authorization_id'
  );

  const authHeaders = async () => {
    const { data } = await supabase.auth.getSession();
    return {
      apikey: import.meta.env.VITE_SUPABASE_KEY as string,
      Authorization: `Bearer ${data.session?.access_token ?? ''}`,
      'Content-Type': 'application/json',
    };
  };

  useEffect(() => {
    if (!authorizationId) {
      setError('Falta el identificador de autorización en la URL.');
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/oauth/authorizations/${authorizationId}`,
          { headers: await authHeaders() }
        );
        if (!res.ok) {
          setError(
            'No se pudo cargar la solicitud. Puede haber expirado; vuelve a intentar la conexión desde la aplicación de origen.'
          );
          return;
        }
        const body: AuthorizationDetails = await res.json();
        // Si el usuario ya le dio permiso a este cliente antes, GoTrue saltea
        // el consentimiento y devuelve directamente el redirect con el code.
        if (body?.redirect_url && !body?.client) {
          window.location.href = body.redirect_url;
          return;
        }
        setDetails(body);
      } catch {
        setError('Error de red cargando la solicitud.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationId]);

  const decide = async (action: 'approve' | 'deny') => {
    if (!authorizationId) return;
    setSubmitting(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/oauth/authorizations/${authorizationId}/consent`,
        {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({ action }),
        }
      );
      const body = await res.json().catch(() => ({}));
      const redirect = body?.redirect_url ?? body?.redirect_to;
      if (res.ok && redirect) {
        window.location.href = redirect;
        return;
      }
      setError('No se pudo completar la operación. Prueba de nuevo.');
      setSubmitting(false);
    } catch {
      setError('Error de red enviando la respuesta.');
      setSubmitting(false);
    }
  };

  const scopes = (details?.scope ?? '')
    .split(' ')
    .filter(Boolean)
    .map((s) => SCOPE_LABELS[s] ?? s);

  return (
    <div className='min-h-screen flex items-center justify-center bg-default-50 p-4'>
      <Card className='w-full max-w-md p-2'>
        <CardHeader className='flex flex-col items-center gap-3 pt-8'>
          <img src={brand.fullLogoLight} alt={brand.name} className='h-10' />
          <h1 className='text-xl font-semibold text-center'>
            Solicitud de conexión
          </h1>
        </CardHeader>
        <CardBody className='gap-4 pb-8'>
          {error ? (
            <p className='text-danger text-sm text-center'>{error}</p>
          ) : !details ? (
            <p className='text-default-400 text-sm text-center'>Cargando…</p>
          ) : (
            <>
              <p className='text-sm text-center'>
                <span className='font-semibold'>
                  {details.client?.name ?? 'Una aplicación externa'}
                </span>{' '}
                quiere conectarse a tu cuenta de {brand.name}
                {details.user?.email ? ` (${details.user.email})` : ''} para:
              </p>
              <ul className='text-sm list-disc list-inside text-default-600'>
                {scopes.map((s) => (
                  <li key={s}>{s}</li>
                ))}
                <li>
                  Leer los datos de mercado y liquidaciones a los que tu
                  usuario ya tiene acceso
                </li>
              </ul>
              <p className='text-xs text-default-400'>
                La aplicación va a ver únicamente lo que tu usuario puede ver.
                Puedes revocar el acceso cuando quieras desde Configuración.
              </p>
              <div className='flex gap-3 justify-center pt-2'>
                <Button
                  color='danger'
                  variant='light'
                  isDisabled={submitting}
                  onClick={() => decide('deny')}
                >
                  Rechazar
                </Button>
                <Button
                  color='primary'
                  isLoading={submitting}
                  onClick={() => decide('approve')}
                >
                  Permitir
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default OAuthConsent;
